import React from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { logButtonClick } from 'features/navigation/utils/analytics';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import ItemUnavailableMessage from 'components/item-unavailable-message';
import { DEFAULT_SLOT_KEY } from 'components/product-detail-v2/product-detail-flow/product-item-flow/constants';
import { useProductAvailability } from 'components/product-detail-v2/use-product-availability';
import { useProductCart } from 'components/product-detail-v2/use-product-cart';
import { NumberInput } from 'components/ucl';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useCart } from 'hooks/use-cart';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import { useRedeemReward } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logRBIEvent,
} from 'state/amplitude';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useEditingCartEntry } from 'state/product-wizard-v2/hooks/use-editing-cart-entry';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';
import { MIN_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';
import { HapticsImpactStyle } from 'utils/haptic';
import { isMenuObjectInAddOnSections } from 'utils/legacy-condiments';

import { ProductCtaWrapper } from '../product-cta-wrapper';
import {
  ActionsWrapper,
  displayValueStyleProps,
  incrementorStyleProps,
} from '../product-cta.styled';
import { getModifierSelections } from '../utils';

import { useGetProductAddToCartCtaLabel } from './product-add-to-cart-cta-label';

interface IProductAddToCartCtaProps {
  disableCta?: boolean;
  modifierSelections: UserSelectionModifierSlotMap | undefined;
}

export const ProductAddToCartCta = ({
  disableCta,
  modifierSelections,
}: IProductAddToCartCtaProps) => {
  const {
    selectedProduct,
    productQuantity,
    setProductQuantity,
    singleOrMultipleModifierSelection,
    menuObject,
  } = useProductWizardContext();
  const { isEdit, rewardBenefitId, currentStep, id } = useProductWizardFlow();
  const { cartEntries } = useCart();
  const { addToOrder } = useProductCart();
  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { incentiveNotInMenu, limitPerOrderMet, rewardPointCost } = useRedeemReward();
  const editingCartEntry = useEditingCartEntry();
  const { isOffer } = useMenuContext();
  const { dayParts, isItemAvailableForDaypart, itemDayParts } = useProductAvailability();
  const { isTakeoverActive } = useAppTakeoverPdp(id);
  const { sections: addOnSections } = useFeatureMenuAddOn({ ignoreEnableAddExtras: true });

  const cartIdEditing = editingCartEntry?.cartId ?? '';
  const { availableCartQuantity, maxCartQuantityMet: isLimitReached } = getMenuObjectCartQuantity({
    menuObject: selectedProduct,
    quantityToAdd: productQuantity,
    cartEntries,
    cartIdEditing,
  });

  const maxCartQuantityMet = isLimitReached && !editingCartEntry;

  const showIncrementor = React.useMemo(
    () => isOffer === false && !maxCartQuantityMet && isItemAvailableForDaypart,
    [isOffer, maxCartQuantityMet, isItemAvailableForDaypart]
  );

  const { formattedProductPrice, totalPrice } = useProductPrice({
    shouldIncludeModifications: true,
    shouldIncludeQuantity: true,
  });

  const lastValueRef = React.useRef(editingCartEntry ? productQuantity : 1);

  const selections = getModifierSelections({
    isEdit,
    singleOrMultipleModifierSelection,
    modifierSelections,
    slotKeyToCustomize: DEFAULT_SLOT_KEY,
  });

  const { disabled, buttonLabel } = useGetProductAddToCartCtaLabel({
    currentStep,
    disableCta,
    formattedProductPrice,
    isEditingCartEntry: !!editingCartEntry,
    maxCartQuantityMet,
    selections,
    singleOrMultipleModifierSelection,
    type: menuObject._type,
  });

  const handleQuantityUpdate = React.useCallback(
    (value: number) => {
      const action = lastValueRef.current < value ? 'increase' : 'decrease';
      setProductQuantity(value);
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: 'Product Incrementor',
        },
      });
      logRBIEvent(
        {
          name: CustomEventNames.ITEM_QUANTITY_MODIFIED,
          type: EventTypes.Other,
          attributes: {
            increaseOrDecrease: action,
            totalQuantity: value,
          },
        },
        { skipLoggingToBraze: true }
      );
      lastValueRef.current = value;
    },
    [setProductQuantity]
  );

  const dispatch = useAppDispatch();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const unApplyExceedingRewards = React.useCallback(() => {
    const rewardApplied = appliedLoyaltyRewards[cartIdEditing];
    if (!rewardApplied || rewardApplied.timesApplied <= productQuantity) {
      return;
    }

    // User decremented the total quantity, so we need to remove the exceeding applied Rewards
    let exceedingRewardsLength = rewardApplied.timesApplied - productQuantity;
    while (exceedingRewardsLength > 0) {
      dispatch(
        actions.loyalty.unApplyReward({
          rewardBenefitId: rewardApplied.rewardId,
          cartId: cartIdEditing,
          loyaltyUser,
        })
      );
      exceedingRewardsLength--;
    }
  }, [appliedLoyaltyRewards, cartIdEditing, dispatch, loyaltyUser, productQuantity]);

  const handlePress = React.useCallback(() => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: editingCartEntry ? 'Product Update' : 'Product Addition',
      },
    });

    if (editingCartEntry) {
      unApplyExceedingRewards();
    }
    addToOrder();
  }, [addToOrder, unApplyExceedingRewards, editingCartEntry]);

  // If a loyalty incentive (offer, reward) is not available as a regular menu item,
  // the incrementor also increments incentive redemptions in addition to adjusting quantity.
  // This takes into account user point and determines whether there are
  // sufficient points for further redemptions.
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);

  const isIncrementDisabledForRewardRedemption = () => {
    if (limitPerOrderMet) {
      return true;
    }
    if (!incentiveNotInMenu || !loyaltyEnabled) {
      return false;
    }
    const checkRedemptionEligibility = () => {
      if (rewardBenefitId) {
        const incrementedPointCost = rewardPointCost * (productQuantity + 1);
        return stagedCartPoints <= incrementedPointCost;
      }
      return false;
    };
    return !!cartIdEditing || checkRedemptionEligibility();
  };

  const disableIncrementQuantity = maxCartQuantityMet || isIncrementDisabledForRewardRedemption();
  const disableIncrementor =
    maxCartQuantityMet || (loyaltyEnabled && incentiveNotInMenu && !!cartIdEditing);

  const productName = selectedProduct?.name?.locale;

  const quantityAriaLabel = t('quantityAriaLabel', { productQuantity, productName });

  const decrementAriaLabel = t('decrementAriaLabel', { productName });
  const incrementAriaLabel = t('incrementAriaLabel', { productName });

  const messageId = editingCartEntry
    ? 'updateQuantityAndItemWithPrice'
    : 'addQuantityAndItemWithPrice';

  const ariaLabel = t(messageId, {
    itemName: productName,
    quantity: productQuantity,
    price: formattedProductPrice,
  });

  const currentTotalPrice = totalPrice();
  const selectedProductId = selectedProduct?._id;
  const matchedCondimentIds = [selectedProductId, menuObject._id].filter(
    (menuObjectId): menuObjectId is string =>
      !!menuObjectId && isMenuObjectInAddOnSections(menuObjectId, addOnSections)
  );
  const isLegacyFreeCondiment = currentTotalPrice <= 0 && matchedCondimentIds.length > 0;
  const isCurrentPriceValid = currentTotalPrice > 0 || isOffer || isLegacyFreeCondiment;

  let Content;

  if (!isItemAvailableForDaypart) {
    Content = (
      <ItemUnavailableMessage
        dayParts={dayParts}
        itemDayParts={itemDayParts}
        marginAndBorders={false}
      />
    );
  } else {
    Content = (
      <ActionsWrapper>
        {showIncrementor && (
          <NumberInput
            displayedValue={productQuantity}
            value={productQuantity}
            onChange={handleQuantityUpdate}
            min={MIN_CART_QUANTITY}
            max={availableCartQuantity}
            hideNumberInput
            disableIncrement={disableIncrementQuantity}
            disabled={disableIncrementor}
            ariaLabel={quantityAriaLabel}
            decrementAriaLabel={decrementAriaLabel}
            incrementAriaLabel={incrementAriaLabel}
            displayValueStyleProps={displayValueStyleProps}
            incrementorStyleProps={incrementorStyleProps}
            hapticFeedbackStyle={HapticsImpactStyle.Light}
          />
        )}

        <ActionButton
          variant={
            isTakeoverActive ? ActionButtonVariants.PRIMARY_TAKEOVER : ActionButtonVariants.PRIMARY
          }
          accessibilityLabel={ariaLabel}
          testID="product-home-action-button"
          disabled={!isCurrentPriceValid || maxCartQuantityMet || disabled}
          onPress={handlePress}
          flex={1}
          marginLeft={showIncrementor ? '$6' : '0'}
        >
          {buttonLabel}
        </ActionButton>
      </ActionsWrapper>
    );
  }

  return <ProductCtaWrapper elements={[{ element: Content }]} />;
};
